import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import { handleRequestError } from '@softline/application';
import {
  DEFAULT_AI_TABLE_EXTRACTOR_CONFIG,
  SOFTLINE_CONFIG_AI_TABLE_EXTRACTOR,
} from '../ai-table-extractor.shared';
import { JobRepository } from '../repositories/job.repository';
import {
  ExtractionResult,
  isSupportedExtractionResult,
  SUPPORTED_EXTRACTION_SCHEMA_VERSION,
} from '../types/extraction-result';
import {
  ExtractionJob,
  JobCreatedResponse,
  JobStatus,
  PageInfo,
  Word,
} from '../types/job';
import { ExtractionMode } from '../types/template';

export class ExtractionPollTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`extraction job did not finish within ${timeoutMs} ms`);
  }
}

export class UnsupportedSchemaVersionError extends Error {
  constructor(version: number | undefined) {
    super(
      `extraction service returned schema version ${version}, ` +
        `supported is ${SUPPORTED_EXTRACTION_SCHEMA_VERSION}`
    );
  }
}

/**
 * Upload + poll state of ONE extraction job. There is no push channel — the
 * service polls `GET /jobs/{id}` until the job completes or fails. Scoped to
 * the module route, so the poll survives the upload → review navigation.
 */
@Injectable()
export class ExtractionJobService {
  private readonly repository = inject(JobRepository);
  private readonly config = inject(SOFTLINE_CONFIG_AI_TABLE_EXTRACTOR);
  private readonly injector = inject(Injector);

  /** Invalidates a running poll loop: bumped on reset/upload/open/destroy. */
  private pollGeneration = 0;

  private readonly _job = signal<ExtractionJob | undefined>(undefined);
  private readonly _uploading = signal(false);
  private readonly _progress = signal(0);
  private readonly _error = signal<Error | undefined>(undefined);
  private readonly _pages = signal<PageInfo[]>([]);
  private readonly _dpi = signal<number | undefined>(undefined);
  private readonly _words = signal<Word[]>([]);
  private readonly _startedAt = signal<number | undefined>(undefined);
  private readonly _templateResult = signal<ExtractionResult | undefined>(undefined);
  private readonly _templateKey = signal<string | undefined>(undefined);
  private readonly _detectedCompany = signal<string | undefined>(undefined);
  private readonly _applyingTemplate = signal(false);
  private readonly _templateError = signal<Error | undefined>(undefined);

  readonly job = this._job.asReadonly();
  readonly status = computed<JobStatus | 'idle'>(() => {
    if (this._uploading()) return 'pending';
    // A completed job whose template is still being applied is not done yet —
    // the review must not render the pipeline result and swap it a moment later.
    if (this._applyingTemplate()) return 'processing';
    return this._job()?.status ?? 'idle';
  });

  /** What the generic pipeline read — kept even when a template supersedes it. */
  readonly pipelineResult = computed<ExtractionResult | undefined>(() => {
    const result = this._job()?.result;
    return isSupportedExtractionResult(result) ? result : undefined;
  });

  /** The working result: the applied supplier template, else the pipeline. */
  readonly result = computed<ExtractionResult | undefined>(() =>
    this._applyingTemplate() ? undefined : (this._templateResult() ?? this.pipelineResult())
  );

  /** Template key applied to the current job, if any. */
  readonly templateKey = this._templateKey.asReadonly();
  /** Company the detection recognized on the document (independent of a template). */
  readonly detectedCompany = this._detectedCompany.asReadonly();
  readonly applyingTemplate = this._applyingTemplate.asReadonly();
  /** Detection/apply failed — the pipeline result stands; never shown as fatal. */
  readonly templateError = this._templateError.asReadonly();
  readonly templateApplied = computed(() => !!this._templateResult());
  readonly extractionMode = computed<ExtractionMode>(() =>
    this._templateResult() ? 'template' : 'pipeline'
  );
  /** Upload progress 0..1. */
  readonly progress = this._progress.asReadonly();
  readonly error = this._error.asReadonly();
  readonly pages = this._pages.asReadonly();
  /** DPI the backend renders the page PNGs at (px = pt * dpi / 72). */
  readonly dpi = this._dpi.asReadonly();
  readonly words = this._words.asReadonly();
  /** Epoch ms of the upload start — drives the elapsed-time display. */
  readonly startedAt = this._startedAt.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.pollGeneration++);
  }

  /** Uploads the document and starts polling. Resolves with the job id. */
  async upload(file: File): Promise<string> {
    this.reset();
    this._uploading.set(true);
    this._startedAt.set(Date.now());
    try {
      const created = await this.uploadFile(file);
      this._job.set({
        id: created.id,
        status: created.status,
        original_name: file.name,
        created_at: Date.now() / 1000,
        completed_at: null,
      });
      // The stored PDF is pageable right away — show the document while the
      // pipeline still runs; completion re-loads pages (rotation gets measured).
      void this.loadPages(created.id, this.pollGeneration);
      void this.poll(created.id);
      return created.id;
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      this._error.set(error);
      handleRequestError(e, this.injector, { show: true });
      throw error;
    } finally {
      this._uploading.set(false);
    }
  }

  /** Resumes an existing job (deep link into review/:jobId). */
  async open(jobId: string): Promise<void> {
    this.reset();
    const generation = this.pollGeneration;
    try {
      const job = await this.repository.get(jobId);
      if (generation !== this.pollGeneration) return;
      this._job.set(job);
      if (job.status === 'completed') await this.onCompleted(job, generation);
      else if (job.status === 'failed')
        this._error.set(new Error(job.error ?? 'extraction failed'));
      else {
        this._startedAt.set(Date.now());
        void this.loadPages(jobId, this.pollGeneration);
        void this.poll(jobId);
      }
    } catch (e) {
      if (generation !== this.pollGeneration) return;
      this._error.set(e instanceof Error ? e : new Error(String(e)));
      handleRequestError(e, this.injector, { show: true });
    }
  }

  /** Deletes the current job on the service and clears all state. */
  async discard(): Promise<void> {
    const jobId = this._job()?.id;
    this.reset();
    if (!jobId) return;
    try {
      await this.repository.delete(jobId);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  private uploadFile(file: File): Promise<JobCreatedResponse> {
    return new Promise<JobCreatedResponse>((resolve, reject) => {
      this.repository.upload(file).subscribe({
        next: (event) => {
          if (event.type === 'progress' && event.total)
            this._progress.set(event.loaded / event.total);
          else if (event.type === 'response' && event.response) {
            this._progress.set(1);
            resolve(event.response);
          }
        },
        error: reject,
        complete: () => reject(new Error('upload finished without a job id')),
      });
    });
  }

  private async poll(jobId: string): Promise<void> {
    const generation = this.pollGeneration;
    const interval =
      this.config.pollIntervalMs ?? DEFAULT_AI_TABLE_EXTRACTOR_CONFIG.pollIntervalMs;
    const timeout =
      this.config.pollTimeoutMs ?? DEFAULT_AI_TABLE_EXTRACTOR_CONFIG.pollTimeoutMs;
    const startedAt = Date.now();

    while (generation === this.pollGeneration) {
      await new Promise((resolve) => setTimeout(resolve, interval));
      if (generation !== this.pollGeneration) return;
      let job: ExtractionJob;
      try {
        job = await this.repository.get(jobId);
      } catch (e) {
        if (generation !== this.pollGeneration) return;
        this._error.set(e instanceof Error ? e : new Error(String(e)));
        handleRequestError(e, this.injector, { show: true });
        return;
      }
      if (generation !== this.pollGeneration) return;
      this._job.set(job);
      if (job.status === 'completed') {
        await this.onCompleted(job, generation);
        return;
      }
      if (job.status === 'failed') {
        this._error.set(new Error(job.error ?? 'extraction failed'));
        return;
      }
      if (Date.now() - startedAt >= timeout) {
        this._error.set(new ExtractionPollTimeoutError(timeout));
        return;
      }
    }
  }

  private async loadPages(jobId: string, generation: number): Promise<void> {
    try {
      const pages = await this.repository.pages(jobId);
      if (generation !== this.pollGeneration) return;
      this._pages.set(pages.pages);
      this._dpi.set(pages.dpi);
    } catch {
      // Pre-completion page geometry is a nicety; completion loads it again.
    }
  }

  private async onCompleted(job: ExtractionJob, generation: number): Promise<void> {
    const version = job.result?.schema_version;
    if (!isSupportedExtractionResult(job.result)) {
      this._error.set(new UnsupportedSchemaVersionError(version));
      return;
    }
    this._applyingTemplate.set(true);
    try {
      // Words are a display nicety (bbox tightening) — their failure must not
      // break the review page, so they fall back to an empty list.
      const [pages, words, template] = await Promise.all([
        this.repository.pages(job.id),
        this.repository.words(job.id).catch(() => ({ words: [] })),
        this.resolveTemplate(job.id),
      ]);
      if (generation !== this.pollGeneration) return;
      this._pages.set(pages.pages);
      this._dpi.set(pages.dpi);
      this._words.set(words.words);
      if (template) this._templateResult.set(template);
    } catch (e) {
      if (generation !== this.pollGeneration) return;
      handleRequestError(e, this.injector, { show: true });
    } finally {
      if (generation === this.pollGeneration) this._applyingTemplate.set(false);
    }
  }

  /**
   * `POST /jobs` ALWAYS runs the generic pipeline; a supplier template is only
   * used when the client asks for it. So after completion we detect the company
   * and, if it has a template, re-read the document with it. Every failure here
   * is silent — the pipeline result is a perfectly usable fallback.
   */
  private async resolveTemplate(jobId: string): Promise<ExtractionResult | null> {
    try {
      const detection = await this.repository.detectApply(jobId);
      this._detectedCompany.set(detection.detection.best?.company ?? undefined);
      const key = detection.chosen_template_key;
      if (!key) return null;
      const result = await this.repository.applyTemplate(jobId, key);
      if (!isSupportedExtractionResult(result)) return null;
      this._templateKey.set(key);
      return result;
    } catch (e) {
      this._templateError.set(e instanceof Error ? e : new Error(String(e)));
      handleRequestError(e, this.injector, { show: false });
      return null;
    }
  }

  private reset(): void {
    this.pollGeneration++;
    this._job.set(undefined);
    this._uploading.set(false);
    this._progress.set(0);
    this._error.set(undefined);
    this._pages.set([]);
    this._dpi.set(undefined);
    this._words.set([]);
    this._startedAt.set(undefined);
    this._templateResult.set(undefined);
    this._templateKey.set(undefined);
    this._detectedCompany.set(undefined);
    this._applyingTemplate.set(false);
    this._templateError.set(undefined);
  }
}
